import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

import Icons from '@/core/components/Icons';
import Photo, { Item } from '@/core/components/Photo';
import SwiperCarouselImage from '@/core/components/SwiperCarouselImage';
import useDisclosure from '@/core/hooks/useDisclosure';
import useKeyDownHandler from '@/core/hooks/useKeyDownHandler';
import { useNativeContext } from '@/core/lib/native/native.context';
import { useRouterContext } from '@/core/lib/router/router.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

export interface NewCarouselImagesProps {
  photos: string[];
}

const NewCarouselImages: React.FC<NewCarouselImagesProps> = ({ photos }) => {
  const { t } = useTranslationContext(['common']);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isNative, platform } = useNativeContext();
  const { beforePopState } = useRouterContext();
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const modalRef = useRef<HTMLDivElement>(null);

  useKeyDownHandler([], 'Escape', (event: KeyboardEvent) => {
    event.preventDefault();
    onClose();
  });

  beforePopState?.(() => {
    document.body.setAttribute('style', '');
    return true;
  });

  useEffect(() => {
    if (isOpen && modalRef.current && !isNative) {
      modalRef.current.focus();
      document.body.setAttribute('style', `position: fixed; top: -${window.scrollY}px; left: 0; right: 0`);
    } else {
      document.body.setAttribute('style', '');
    }
  }, [isOpen]);

  const imageType = platform === 'ios' ? 'jpg' : 'webp';
  const id = useId();
  const images = useMemo(() => {
    return photos.map(
      (photo, index) =>
        ({
          src: photo.replace('{{size}}', '8').replace('{{ext}}', imageType),
          alt: t('components.carousel-images.image.alt', { ns: 'common', donationNumber: String(index + 1) }),
        }) satisfies Item,
    );
  }, [photos]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        className="relative flex h-67 justify-center gap-6 overflow-y-hidden bg-bg-secondary lg:gap-7 xl:h-82 2xl:h-120"
        aria-label={t('components.carousel-images', { ns: 'common', currentIndex: activeIndex.toString() })}
        tabIndex={-1}
        role="button"
        onClick={onOpen}
      >
        <Photo index={9999999} item={images[activeIndex]} key={images[activeIndex].src} className="size-full object-cover lg:object-contain" containerClassName="size-full" />
        <div
          className="absolute bottom-4 right-4 flex w-20 items-center justify-between rounded-1 bg-content-primary p-2 text-bg-pale"
          aria-label={t('components.carousel-images.full-screen-button.label', { ns: 'common' })}
        >
          <Icons icon="camera" size="20" color="current" />
          <span className="ml-1">
            {activeIndex + 1} / {photos.length}
          </span>
        </div>
      </div>

      {isOpen && (
        <div ref={modalRef} data-dialog-id={id} className="fixed right-0 top-0 z-50 !m-0 size-full justify-items-center overflow-auto bg-bg-dark focus-visible:border-none focus-visible:outline-none">
          <div role="dialog" className="w-full">
            <SwiperCarouselImage images={images} onClose={onClose} setActiveIndex={setActiveIndex} activeIndex={activeIndex} />
          </div>
        </div>
      )}
    </>
  );
};

export default NewCarouselImages;
